import { Alert, Button, Card, Input, Space, Typography } from "antd";
import { useEffect, useState, type ChangeEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router";

import request from "@/api";
import { DATETIME_PICKER_FORMAT } from "@/config";
import { dateTimeFormat } from "@/utils";
import type { Refund, RefundApproveForm } from "@zhihaoscm/entity";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Dialog as ZhDialog,
    Grid as ZhGrid
} from "zhihao-ui-react";

export interface TradeRefundDetailPageProps {
    loadDetail?: (id: number | string) => Promise<Refund>;
    approveRefund?: (id: number | string, payload: RefundApproveForm) => Promise<unknown>;
    cancelRefund?: (id: number | string) => Promise<unknown>;
    previewRefundFile?: (id: number | string) => Promise<string | undefined>;
    permissions?: string[];
}

const REFUND_STATE_LABELS: Record<number, string> = {
    1: "待审核",
    2: "待退款",
    3: "已驳回",
    4: "已退款"
};

/** 将退款金额格式化为 Vue 详情页使用的金额文案。 */
function formatMoney(value: number | undefined): string {
    return `${(Number(value) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}元`;
}

/** 沿用 Vue 退款详情的统一日期时间格式，隐藏接口时间中的秒数。 */
function formatRefundDateTime(value?: string): string {
    return dateTimeFormat(value, DATETIME_PICKER_FORMAT);
}

/** 沿用 Vue BaseService.get 的退款详情接口路径。 */
export async function loadTradeRefundDetail(id: number | string): Promise<Refund> {
    const response = await request.service.get<Refund>(`/admin/refund/${id}`);
    return response.data;
}

/** 沿用 Vue `PUT /admin/refund/approve/:id` 审核退款接口。 */
export async function approveTradeRefund(id: number | string, payload: RefundApproveForm): Promise<unknown> {
    const response = await request.service.put(`/admin/refund/approve/${id}`, payload);
    return response.data;
}

/** 沿用 Vue 退款详情中的取消操作接口。 */
export async function cancelTradeRefundDetail(id: number | string): Promise<unknown> {
    const response = await request.service.delete(`/admin/refund/${id}`);
    return response.data;
}

/** 沿用 Vue 退款凭证文件服务并返回可打开地址。 */
export async function previewTradeRefundDetailFile(id: number | string): Promise<string | undefined> {
    const response = await request.service.get<{ path?: string }>(`/dataService/admin/file/${id}`);
    return response.data.path;
}

/** 将采购企业信息映射为 Vue 退款详情中的采购账号文案。 */
function getCustomerLabel(detail: Refund): string {
    const customer = detail.customerEnterprise;
    return `${customer?.name || customer?.realName || "-"} (${customer?.mobile || "-"})`;
}

/** 沿用 Vue 退款详情按商品类型跳转对应商品详情页的规则。 */
function getProductDetailPath(detail: Refund): string | undefined {
    if (!detail.productId) return undefined;
    const productPath =
        detail.productType === 1
            ? "consignment"
            : detail.productType === 2
              ? "groupPurchase"
              : detail.productType === 3
                ? "bargain"
                : undefined;
    return productPath ? `/productManagement/${productPath}/detail?id=${detail.productId}` : undefined;
}

/** 交易管理退款详情首屏，保留 Vue 的关联、退款和审核信息分组。 */
export function TradeRefundDetailPage({
    loadDetail = loadTradeRefundDetail,
    approveRefund = approveTradeRefund,
    cancelRefund = cancelTradeRefundDetail,
    previewRefundFile = previewTradeRefundDetailFile,
    permissions = []
}: TradeRefundDetailPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const refundId = new URLSearchParams(location.search).get("id") || "";
    const isAuditPage = location.pathname.endsWith("/refund/audit");
    const [detail, setDetail] = useState<Refund | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [auditState, setAuditState] = useState<number | null>(null);
    const [auditRemark, setAuditRemark] = useState("");
    const [auditFileId, setAuditFileId] = useState("");
    const [auditFileName, setAuditFileName] = useState("");

    const hasPermission = (permission: string) => permissions.includes(permission);

    /** 打开 Vue 审核意见弹窗，并记录本次审核目标状态。 */
    const openAudit = (state: number) => {
        setAuditState(state);
        setAuditRemark("");
        setAuditFileId("");
        setAuditFileName("");
        setErrorMessage(null);
    };

    /** 沿用 Vue SingleUpload 的 FormData 契约，上传后把文件 ID 回填到审核 payload。 */
    const uploadAuditVoucher = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        const formData = new FormData();
        formData.append("file", file);
        try {
            // 按需加载文件服务，避免历史详情页的上传模块阻断路由测试和首屏加载。
            const { fileService } = await import("@/api/services/fileService");
            const uploadedFile = await fileService.uploadFile(formData);
            setAuditFileId(String(uploadedFile.id));
            setAuditFileName(uploadedFile.name || file.name);
            setErrorMessage(null);
        } catch {
            setErrorMessage("退款凭证上传失败");
        } finally {
            event.target.value = "";
        }
    };

    /** 提交 Vue 审核表单，驳回必须填写意见，同意必须填写退款凭证。 */
    const submitAudit = async () => {
        if (!refundId || auditState === null) return;
        if (auditState === 3 && !auditRemark.trim()) {
            setErrorMessage("请输入审核意见");
            return;
        }
        if (auditState === 4 && !Number(auditFileId)) {
            setErrorMessage("请上传退款凭证");
            return;
        }
        try {
            await approveRefund(refundId, {
                state: auditState,
                approveRemark: auditRemark.trim(),
                fileId: Number(auditFileId) || 0
            });
            setAuditState(null);
            navigate(-1);
        } catch {
            setErrorMessage("退款审核提交失败");
        }
    };

    /** 沿用 Vue 取消退款动作，成功后返回上级页面。 */
    const cancel = async () => {
        if (!refundId) return;
        try {
            await cancelRefund(refundId);
            navigate(-1);
        } catch {
            setErrorMessage("退款取消失败");
        }
    };

    /** 打开 Vue 退款凭证地址。 */
    const preview = async () => {
        if (!detail?.fileId) return;
        try {
            const path = await previewRefundFile(detail.fileId);
            if (path) window.open(path, "_blank");
        } catch {
            setErrorMessage("退款凭证预览失败");
        }
    };

    useEffect(() => {
        if (!refundId) {
            setErrorMessage("缺少退款编号");
            return;
        }
        let disposed = false;
        void loadDetail(refundId)
            .then(result => {
                if (!disposed) {
                    setDetail({ ...result, state: Number(result.state) });
                    setErrorMessage(null);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("退款详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadDetail, refundId]);

    return (
        <section aria-label="trade-refund-detail-page" className="flex h-full min-h-full flex-col">
            <Card>
                <Typography.Title className="mb-4 mt-0" level={4}>
                    {isAuditPage ? "审核退款" : "退款详情"}
                </Typography.Title>
                {errorMessage && auditState === null ? (
                    <Alert message={errorMessage} role="alert" type="error" />
                ) : null}
                {detail ? (
                    <>
                        <div className="section-card">
                            <ZhDetailHeader
                                leftInfos={[{ label: "编号", value: detail.id || "-" }]}
                                rightInfos={[{ label: "创建时间", value: formatRefundDateTime(detail.createdTime) }]}
                                stateDesc={REFUND_STATE_LABELS[detail.state] || "-"}
                                title={isAuditPage ? "审核退款" : "退款详情"}
                            />
                        </div>
                        <div className="section-card mt-4">
                            <div className="section-title mb-5">关联信息</div>
                            <ZhGrid cols={4} gap={[30, 20]}>
                                <ZhBaseItem label="关联订单">
                                    {detail.orderId ? (
                                        <Link to={`/tradeManage/tradeOrder/detail?id=${detail.orderId}`}>
                                            {detail.orderId}
                                        </Link>
                                    ) : (
                                        "-"
                                    )}
                                </ZhBaseItem>
                                <ZhBaseItem label="关联商品">
                                    {getProductDetailPath(detail) ? (
                                        <Link to={getProductDetailPath(detail) as string}>{detail.productId}</Link>
                                    ) : (
                                        detail.productId || "-"
                                    )}
                                </ZhBaseItem>
                                <ZhBaseItem label="采购账号">
                                    {detail.customerId ? (
                                        <Link to={`/lianYunUser/userInfo?userId=${detail.customerId}`}>
                                            {getCustomerLabel(detail)}
                                        </Link>
                                    ) : (
                                        getCustomerLabel(detail)
                                    )}
                                </ZhBaseItem>
                            </ZhGrid>
                        </div>
                        <div className="section-card mt-4">
                            <div className="section-title mb-5">退款信息</div>
                            <ZhGrid cols={4} gap={[30, 20]}>
                                <ZhBaseItem label="退款类型" value={detail.refundType || "-"} />
                                <ZhBaseItem label="退款金额" value={formatMoney(detail.amount)} />
                                {detail.state === 4 ? (
                                    <ZhBaseItem label="退款时间" value={formatRefundDateTime(detail.refundTime)} />
                                ) : null}
                                <ZhBaseItem label="退款说明" span={4} value={detail.remark || "-"} />
                            </ZhGrid>
                        </div>
                        {detail.state !== 1 && !isAuditPage ? (
                            <div className="section-card mt-4">
                                <div className="section-title mb-5">审核信息</div>
                                <ZhGrid cols={4} gap={[30, 20]}>
                                    <ZhBaseItem label="审核人" value={detail.approveName || "-"} />
                                    <ZhBaseItem label="审核时间" value={formatRefundDateTime(detail.approveTime)} />
                                    <ZhBaseItem label="退款凭证">
                                        {detail.fileId ? (
                                            <Button aria-label="查看/下载" type="link" onClick={() => void preview()}>
                                                查看/下载
                                            </Button>
                                        ) : (
                                            "-"
                                        )}
                                    </ZhBaseItem>
                                    {detail.approveRemark ? (
                                        <ZhBaseItem label="审核意见" span={4} value={detail.approveRemark} />
                                    ) : null}
                                </ZhGrid>
                            </div>
                        ) : null}
                        <div className="section-card mt-4">
                            <div className="section-title mb-5">银行账户</div>
                            <ZhGrid cols={4} gap={[30, 20]}>
                                <ZhBaseItem
                                    label="付款银行"
                                    value={
                                        detail.payBankInfo?.openingBank ||
                                        (detail.payBankInfo as unknown as { bank?: string })?.bank ||
                                        "-"
                                    }
                                />
                                <ZhBaseItem label="收款开户名" value={detail.receiveBankInfo?.name || "-"} />
                                <ZhBaseItem label="收款银行" value={detail.receiveBankInfo?.bank || "-"} />
                            </ZhGrid>
                        </div>
                    </>
                ) : null}
                {detail && isAuditPage ? (
                    <Space className="mt-4">
                        <Button aria-label="同意" onClick={() => openAudit(4)} type="primary">
                            同意
                        </Button>
                        <Button aria-label="驳回" onClick={() => openAudit(3)}>
                            驳回
                        </Button>
                    </Space>
                ) : null}
                {detail && !isAuditPage ? (
                    <Space className="mt-4">
                        {Number(detail.state) === 1 && hasPermission("ROLE_REFUND_DEAL") ? (
                            <Button
                                aria-label="审核"
                                onClick={() => navigate(`/tradeManage/refund/audit?id=${refundId}`)}
                            >
                                审核
                            </Button>
                        ) : null}
                        {Number(detail.state) === 3 && hasPermission("ROLE_REFUND_MANAGE") ? (
                            <Button
                                aria-label="修改"
                                onClick={() => navigate(`/tradeManage/refund/edit?id=${refundId}`)}
                            >
                                修改
                            </Button>
                        ) : null}
                        {[1, 3].includes(Number(detail.state)) && hasPermission("ROLE_REFUND_DEAL") ? (
                            <Button aria-label="取消" onClick={() => void cancel()}>
                                取消
                            </Button>
                        ) : null}
                    </Space>
                ) : null}
                <Button className="mt-4" onClick={() => navigate(-1)}>
                    返回
                </Button>
                <ZhDialog
                    open={auditState !== null}
                    title={auditState === 4 ? "审核通过" : "审核驳回"}
                    width={400}
                    onCancel={() => setAuditState(null)}
                    footer={
                        <Space>
                            <Button onClick={() => setAuditState(null)}>取消</Button>
                            <Button aria-label="提交" onClick={() => void submitAudit()} type="primary">
                                提交
                            </Button>
                        </Space>
                    }
                >
                    {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                    <label className="block">
                        审核意见
                        <Input.TextArea
                            aria-label="审核意见"
                            className="mt-2"
                            maxLength={100}
                            rows={3}
                            value={auditRemark}
                            onChange={event => setAuditRemark(event.target.value)}
                        />
                    </label>
                    {auditState === 4 ? (
                        <label className="mt-4 block">
                            退款凭证文件
                            <input
                                aria-label="退款凭证文件"
                                accept="image/*,.pdf,.doc,.docx"
                                className="mt-2 block"
                                type="file"
                                onChange={event => void uploadAuditVoucher(event)}
                            />
                            {auditFileName ? <span className="mt-2 block">{auditFileName}</span> : null}
                            <span className="mt-2 block">退款凭证文件ID</span>
                            <Input
                                aria-label="退款凭证文件ID"
                                className="mt-2"
                                type="number"
                                value={auditFileId}
                                onChange={event => setAuditFileId(event.target.value)}
                            />
                        </label>
                    ) : null}
                </ZhDialog>
            </Card>
        </section>
    );
}

export default TradeRefundDetailPage;
